import type { ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { LinkButton } from '../ui/Button.js';
import { signInUrl } from '../api.js';
import { safeReturnPath } from '../../shared/text.js';
import { AppMark } from './AppMark.js';
import { RegionErrorBoundary } from './RegionErrorBoundary.js';
import { RouteFocus } from './RouteFocus.js';
import { SkipLink } from './SkipLink.js';

// Why its own module: MeProvider renders this shell around the sign-in page when /api/me answers
// 401, and AppShell's account menu reads MeProvider's context — kept in AppShell.tsx, the two
// would import each other in a cycle.

export interface PublicShellProps {
  /** The sign-in page, rendered in place by MeProvider; the public routes render through the outlet instead. */
  children?: ReactNode;
  /** Where the top bar's "Sign in" returns to after sign-in; the sign-in page passes the URL it stands at. */
  signInReturnTo?: string;
}

/**
 * The return path the development sign-in page stands for — its own `returnTo`, made safe, or `/` — and null on any other
 * page. Exported for test/server/client-pages/public-shell-slot.test.ts.
 */
export function devSignInReturnTo(pathname: string, search: string): string | null {
  if (!pathname.startsWith('/dev-sign-in')) return null;
  return safeReturnPath(new URLSearchParams(search).get('returnTo')) ?? '/';
}

/**
 * The signed-out shell — for /signed-out and /dev-sign-in, and around the sign-in page at any URL
 * visited without a session: the same top bar, with the sign-in action in the account slot because
 * there is no signed-in user (03-components §15.1). No rail and no tab bar: there is nowhere to go.
 */
export function PublicShell({ children, signInReturnTo = '/' }: PublicShellProps) {
  const location = useLocation();
  // Why the slot is never empty, the development sign-in page included (03-components §15.1: "Where the app has no
  // signed-in user, the slot carries the sign-in action"; Phase 1c finding R18): it is the same action on every signed-out
  // page. On /dev-sign-in it carries that page's own return path, so the round trip — which the server answers with this
  // page while AUTH_MODE=dev, and with Google otherwise — never drops where the person was going.
  const returnTo = devSignInReturnTo(location.pathname, location.search) ?? signInReturnTo;
  return (
    <div className="app-shell app-shell--public">
      <SkipLink />
      <div className="shell-top">
        <header className="top-bar">
          <AppMark className="top-bar__mark" />
          <div className="top-bar__space" />
          <div className="top-bar__account">
            {/* Secondary: the page body carries the view's one primary — the landing's card button,
                /signed-out's sheet button (03-components §2) — and the bar's control is the standing
                alternative to it. */}
            <LinkButton href={signInUrl(returnTo)} variant="secondary" size="sm">Sign in</LinkButton>
          </div>
        </header>
      </div>
      <main id="main" className="shell-main shell-main--public" tabIndex={-1}>
        <RouteFocus />
        <RegionErrorBoundary region="this page" resetKey={location.pathname}>
          {children ?? <Outlet />}
        </RegionErrorBoundary>
      </main>
    </div>
  );
}
